// ── جسر التطبيق النيتف (Android / iOS) ───────────────────────────────────────
//
// التطبيقان مبنيان بـ Capacitor في مجلد `mobile/` بجذر المستودع، ويُحمّلان الموقع
// اللايف نفسه داخل غلاف نيتف (server.url في mobile/capacitor.config.json). لذلك:
//
//   • أي تعديل تنشره على Vercel يظهر في التطبيق فوراً — بدون رفع نسخة للمتاجر.
//   • هذا الملف **لا يستورد أي حزمة npm**: الجسر النيتف يحقن `window.Capacitor`
//     ومعه كل الإضافات داخل الـ WebView وقت التشغيل. صفر بايت على باندل الويب،
//     ولا أي أثر على سرعة أول تحميل للمتجر أو اللوحة (قاعدة الأداء في CLAUDE.md).
//   • على المتصفح العادي كل الدوال هنا تعمل كـ no-op وترجع قيماً محايدة.
//
// أسماء الإضافات المتاحة (كما هي مثبّتة في mobile/package.json):
//   App · StatusBar · SplashScreen · Keyboard · Browser · Share · Haptics
//   Network · Camera · FirebaseMessaging

const cap = () => (typeof window !== "undefined" ? window.Capacitor : null);

/** هل نحن داخل تطبيق أندرويد/iOS فعلاً؟ (لا يشمل الويب ولا PWA) */
export const isNative = () => {
  try { return !!cap()?.isNativePlatform?.(); } catch { return false; }
};

/** "android" | "ios" | "web" — يُستخدم أيضاً لوسم توكن الإشعارات بمنصّته. */
export const platform = () => {
  try { return isNative() ? cap().getPlatform() : "web"; } catch { return "web"; }
};

/** إضافة Capacitor بالاسم، أو null على الويب / إن لم تكن مثبّتة. */
export const plug = (name) => {
  try { return cap()?.Plugins?.[name] || null; } catch { return null; }
};

// ── المشاركة ────────────────────────────────────────────────────────────────
// WebView أندرويد لا يدعم navigator.share، فكانت المشاركة تسقط على النسخ.
// داخل التطبيق نفتح شيت المشاركة النيتف (واتساب وغيره) عبر إضافة Share.

/**
 * يفتح شيت المشاركة لنص: نيتف داخل التطبيق ← navigator.share على الويب ← نسخ.
 * يرجّع "shared" | "copied" | "cancelled" | "failed".
 */
export const shareText = async (text) => {
  const Share = plug("Share");
  if (Share) {
    try { await Share.share({ text, dialogTitle: "مشاركة" }); return "shared"; }
    catch (e) { if (/cancel/i.test(String(e?.message || e))) return "cancelled"; }
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    try { await navigator.share({ text }); return "shared"; }
    catch (e) { if (e?.name === "AbortError") return "cancelled"; }
  }
  try { await navigator.clipboard.writeText(text); return "copied"; } catch { return "failed"; }
};

// ── إشعارات الدفع النيتف ─────────────────────────────────────────────────────
// على الويب: FCM عبر Service Worker (utils/notifications.js).
// على الجوال: نفس مشروع Firebase لكن عبر @capacitor-firebase/messaging، وهو يرجّع
// توكن FCM على المنصّتين (لا توكن APNs الخام) — فيبقى /api/send-notification كما هو.

/** يجب أن تطابق default_notification_channel_id في AndroidManifest.xml */
export const ANDROID_CHANNEL_ID = "velorci_orders";

let _channelReady = false;

/**
 * يطلب الإذن (إن سُمح) ويرجّع توكن FCM لهذا الجهاز، أو null.
 * prompt=false ⇒ لا تُعرض أي نافذة إذن؛ نرجّع التوكن فقط إن كان الإذن ممنوحاً سابقاً.
 */
export const nativePushToken = async ({ prompt = false } = {}) => {
  const FM = plug("FirebaseMessaging");
  if (!FM) return null;
  try {
    let perm = await FM.checkPermissions();
    if (perm?.receive !== "granted") {
      if (!prompt) return null;
      perm = await FM.requestPermissions();
    }
    if (perm?.receive !== "granted") return null;

    // أندرويد 8+: بدون قناة لا تظهر الإشعارات إطلاقاً. أولوية عالية ليصل تنبيه الطلب فوراً.
    if (platform() === "android" && !_channelReady) {
      _channelReady = true;
      try {
        await FM.createChannel({
          id: ANDROID_CHANNEL_ID,
          name: "الطلبات والتنبيهات",
          description: "تنبيهات الطلبات الجديدة ورسائل واتساب",
          importance: 5,        // MAX — تظهر كتنبيه منبثق
          visibility: 1,        // PUBLIC
          vibration: true,
          lights: true,
        });
      } catch {}
    }

    const { token } = await FM.getToken();
    return token || null;
  } catch (e) {
    console.error("nativePushToken:", e);
    return null;
  }
};

/** حالة إذن الإشعارات النيتف: "granted" | "denied" | "prompt". */
export const nativePushPermission = async () => {
  const FM = plug("FirebaseMessaging");
  if (!FM) return "unsupported";
  try { return (await FM.checkPermissions())?.receive || "prompt"; } catch { return "prompt"; }
};

/** يحذف توكن هذا الجهاز من FCM (عند تسجيل الخروج). */
export const nativePushDelete = async () => {
  const FM = plug("FirebaseMessaging");
  if (!FM) return;
  try { await FM.deleteToken(); } catch {}
};

// ── شريط إشعار داخل التطبيق (للإشعارات الواصلة والتطبيق مفتوح) ───────────────
// DOM خام لا React: يعمل من أي شاشة بلا ربط بمكوّن، ولا يضيف شيئاً لباندل الويب
// (يُستدعى داخل التطبيق النيتف وحده). ألوان البراند: تيل/مِنت/تركوازي.
const openLink = (link) => {
  if (link && typeof link === "string" && link.startsWith("/") && !link.startsWith("//")) {
    try { window.history.pushState({}, "", link); window.dispatchEvent(new PopStateEvent("popstate")); } catch {}
  }
};

let _bannerTimer = null;
const showInAppBanner = (title, body, link) => {
  if (!title) return;
  try {
    let el = document.getElementById("velorci-push-banner");
    if (!el) {
      el = document.createElement("div");
      el.id = "velorci-push-banner";
      el.setAttribute("role", "status");
      el.style.cssText = "position:fixed;left:12px;right:12px;top:calc(10px + env(safe-area-inset-top));z-index:2147483000;"
        + "background:#083A3F;color:#E8FFFE;border:1px solid rgba(101,212,210,.45);border-radius:14px;padding:11px 14px;"
        + "box-shadow:0 8px 24px rgba(4,19,22,.35);font-family:Cairo,sans-serif;direction:rtl;cursor:pointer;"
        + "transition:transform .25s ease,opacity .25s ease;transform:translateY(-140%);opacity:0;";
      const t = document.createElement("div"); t.style.cssText = "font-size:13.5px;font-weight:800;color:#65D4D2;margin-bottom:2px;";
      const b = document.createElement("div"); b.style.cssText = "font-size:12.5px;line-height:1.5;max-height:3em;overflow:hidden;";
      el.appendChild(t); el.appendChild(b);
      document.body.appendChild(el);
    }
    const [t, b] = el.children;
    t.textContent = String(title);          // textContent لا innerHTML: النص قد يكتبه عميل (رسالة واتساب)
    b.textContent = String(body || "");
    const hide = () => { el.style.transform = "translateY(-140%)"; el.style.opacity = "0"; };
    el.onclick = () => { hide(); openLink(link); };
    requestAnimationFrame(() => { el.style.transform = "translateY(0)"; el.style.opacity = "1"; });
    try { plug("Haptics")?.vibrate?.({ duration: 200 }); } catch {}
    clearTimeout(_bannerTimer);
    _bannerTimer = setTimeout(hide, 6000);
  } catch {}
};

// ── التهيئة عند إقلاع التطبيق ────────────────────────────────────────────────

let _inited = false;

/**
 * تُستدعى مرة واحدة من src/index.js. على المتصفح ترجع فوراً بلا أي عمل.
 * كل ما بداخلها غير متزامن وخفيف — لا يعطّل الرندر الأول.
 */
export const initNative = () => {
  if (_inited || !isNative()) return;
  _inited = true;

  const p = platform();
  try {
    // وسم للـ CSS: يسمح بتعديلات صغيرة خاصة بالتطبيق دون التأثير على الويب.
    document.documentElement.classList.add("velorci-native", `velorci-${p}`);
  } catch {}

  // شاشة البداية: نخفيها فور جاهزية الواجهة بدل انتظار المهلة القصوى.
  const SplashScreen = plug("SplashScreen");
  if (SplashScreen) {
    const hide = () => { try { SplashScreen.hide(); } catch {} };
    if (document.readyState === "complete") setTimeout(hide, 120);
    else window.addEventListener("load", () => setTimeout(hide, 120), { once: true });
  }

  // شريط الحالة بألوان البراند (خلفية مِنت + أيقونات داكنة).
  const StatusBar = plug("StatusBar");
  if (StatusBar) {
    try {
      StatusBar.setStyle({ style: "LIGHT" });   // LIGHT = محتوى داكن على خلفية فاتحة
      if (p === "android") StatusBar.setBackgroundColor({ color: "#E8FFFE" });
    } catch {}
  }

  const App = plug("App");
  if (App) {
    // زر الرجوع في أندرويد: نرجع خطوة في التطبيق، وعند الجذر نُصغّر التطبيق
    // بدل إغلاقه (الإغلاق المفاجئ عند أول ضغطة تجربة سيئة ويفقد حالة الشاشة).
    try {
      App.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack || window.history.length > 1) window.history.back();
        else App.minimizeApp();
      });
    } catch {}

    // عودة التطبيق للمقدّمة: نطلق نفس حدث الويب حتى يجدّد التطبيق التوكن والبيانات.
    try {
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) document.dispatchEvent(new Event("visibilitychange"));
      });
    } catch {}
  }

  // إشعارات واصلة والتطبيق مفتوح، أو ضغط المستخدم على الإشعار.
  const FM = plug("FirebaseMessaging");
  if (FM) {
    const emit = (notification) => {
      const d = notification?.data || {};
      window.dispatchEvent(new CustomEvent("fcm-message", {
        detail: { notification: { title: notification?.title, body: notification?.body }, data: d },
      }));
      return d;
    };
    // التطبيق مفتوح: أندرويد لا يعرض إشعار FCM والتطبيق في المقدّمة (يسلّمه للتطبيق
    // فقط)، ولم يكن أحد يعرضه — فيضيع الإشعار، ويبدو زر «تجربة على هذا الجهاز» معطّلاً
    // دائماً لأنه يُضغط والتطبيق مفتوح. نعرض شريطاً داخل التطبيق بدلاً منه.
    try {
      FM.addListener("notificationReceived", (e) => {
        const d = emit(e?.notification);
        showInAppBanner(e?.notification?.title || d.title, e?.notification?.body || d.body, d.link || d.url);
      });
    } catch {}
    try {
      FM.addListener("notificationActionPerformed", (e) => {
        const d = emit(e?.notification);
        const link = d.link || d.url;
        if (link && typeof link === "string" && link.startsWith("/")) {
          try { window.history.pushState({}, "", link); window.dispatchEvent(new PopStateEvent("popstate")); } catch {}
        }
      });
    } catch {}
    // تغيّر التوكن (تدويره من FCM): نعيد التسجيل فوراً حتى لا تتوقف الإشعارات صامتةً.
    try {
      FM.addListener("tokenReceived", () => {
        window.dispatchEvent(new CustomEvent("native-push-token-changed"));
      });
    } catch {}
  }
};
